import { makeAutoObservable, runInAction } from 'mobx';

import { giftingService } from '@/services/gifting';
import { ApiError } from '@/services/errors';
import { authStore } from '@/stores/auth.store';
import type { CampaignCopy, GiftProduct, GiftShippingAddress } from '@/types/gifting';

/**
 * Season of Gifting state: the product catalog, the in-progress shipping
 * draft, and the claim action.
 *
 * The shipping draft lives HERE (not in screen state) so switching products
 * between confirm attempts preserves what the athlete already typed. It is
 * cleared on logout via reset() — wired in app/_layout.tsx next to
 * snippetsStore — so a second athlete on a shared phone never inherits the
 * previous user's address.
 */
const EMPTY_ADDRESS: GiftShippingAddress = {
  full_name: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
};

class GiftingStore {
  products: GiftProduct[] = [];
  /**
   * Server-owned campaign identity strings from the catalog response — the
   * logged-out landing's copy source (no user payload exists yet). Screens
   * pass this through lib/campaignCopy, which fills gaps from the shipped
   * fallback.
   */
  campaignCopy: Partial<CampaignCopy> | null = null;
  loading = false;
  loaded = false;
  loadError: ApiError | null = null;

  claiming = false;

  shippingDraft: GiftShippingAddress = { ...EMPTY_ADDRESS };

  constructor() {
    makeAutoObservable(this);
  }

  get soldOutCount(): number {
    return this.products.filter((p) => p.sold_out).length;
  }

  /** True when every listed product is claimed out — drives the page-level "all gifts claimed" state. */
  get allSoldOut(): boolean {
    return this.products.length > 0 && this.soldOutCount === this.products.length;
  }

  productById(id: number): GiftProduct | undefined {
    return this.products.find((p) => p.id === id);
  }

  async load(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.loadError = null;
    try {
      const { products, campaignCopy } = await giftingService.fetchProducts();
      runInAction(() => {
        this.products = products;
        this.campaignCopy = campaignCopy;
        this.loaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof ApiError ? err : null;
        if (!(err instanceof ApiError)) throw err;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async ensureLoaded(): Promise<void> {
    if (this.loading) return;
    // A completed-but-empty catalog is retried on the next mount: the list
    // is [] both pre-launch and pre-login for ineligible callers, so an
    // early empty response must not stick for the whole app session once
    // the user signs in / becomes eligible (review finding).
    if (this.loaded && this.products.length > 0) return;
    await this.load();
  }

  updateShippingDraft(patch: Partial<GiftShippingAddress>): void {
    this.shippingDraft = { ...this.shippingDraft, ...patch };
  }

  /**
   * Claim a product. On success the fresh claim (with product snapshot) is
   * written onto the auth store's user so the home card and secured screen
   * react without a /me round-trip; the server payload remains the source
   * of truth on next refresh.
   *
   * Throws ApiError for the caller to branch on:
   *   sold_out        → refresh the grid, send the user back to pick again
   *   already_claimed → route to secured (their claim is on the next /me)
   */
  async claim(giftProductId: number): Promise<void> {
    if (this.claiming) return;
    this.claiming = true;
    try {
      const claim = await giftingService.createClaim(giftProductId, this.normalizedDraft());
      runInAction(() => {
        if (authStore.user) {
          authStore.user.gift_claim = claim;
          // Record what the server's next payload WILL say (a claim always
          // routes to 'secured') — not recomputed policy, just the confirmed
          // state transition. Without this the cached step stays 'choose' and
          // the grid re-renders for a user who already claimed.
          authStore.user.gifting_next_step = 'secured';
        }
      });
    } finally {
      runInAction(() => {
        this.claiming = false;
      });
    }
  }

  /** Refresh sold-out flags after a sold_out race so the grid is honest. */
  async refreshAfterSoldOut(): Promise<void> {
    this.loaded = false;
    await this.load();
  }

  reset(): void {
    this.products = [];
    this.loading = false;
    this.loaded = false;
    this.loadError = null;
    this.claiming = false;
    this.shippingDraft = { ...EMPTY_ADDRESS };
  }

  private normalizedDraft(): GiftShippingAddress {
    const d = this.shippingDraft;
    return {
      full_name: d.full_name.trim(),
      street: d.street.trim(),
      street2: d.street2?.trim() || undefined,
      city: d.city.trim(),
      state: d.state.trim().toUpperCase(),
      zip: d.zip.trim(),
    };
  }
}

export const giftingStore = new GiftingStore();
